import { DestroyRef, Injectable, Inject, LOCALE_ID } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DatabaseCacheService, Breadcrumb } from '../../../data/cache/database-cache.service';
import { HomeUserListStateService, UserListViewMode } from '../../../services/home-user-list-state.service';
import { PaginationService } from '@app/core/http/pagination.service';
import { ScrollService } from '@app/core/layout/scroll.service';
import { MsalService } from '@azure/msal-angular';
import { BaseViewModel, BaseViewModelState } from '../../../shared/view-model/base-view-model';
import { HOME_USER_LIST_I18N } from './home-user-list.i18n';
import { getTimestamp, parseDateInput } from '@app/core/date/date-time.util';
import {
  buildRecordTypeSearchText,
  getRecordTypePrimaryLabel,
} from '../user-record-list/record-type-display.util';

export type SortDirection = 'asc' | 'desc' | '';

export interface UserListRow {
  user: {
    userId: string;
    code: string;
    lastName?: string;
    firstName?: string;
    sex?: string;
    birthDate?: string;
  };
  userStatistics?: {
    data?: {
      lastUpdatedProtocol?: string;
      lastUpdatedProtocolCreatedAt?: string;
    };
  };
  userData?: Record<string, { data?: Record<string, string> } | null>;
}

export interface UserDataAttribute {
  enabled: boolean;
  showOnSummary: boolean;
  name: string;
  type: string;
}

export interface UserDataAttributeGroup {
  dataItems: UserDataAttribute[];
}

export interface ColumnDefinition {
  def: string;
  label: string;
  visible: boolean;
  /** Columns that cannot be hidden (e.g. userRecord, actions) */
  locked?: boolean;
}

export interface HomeUserListState extends BaseViewModelState {
  breadcrumbs: Breadcrumb[];
  filterText: string;
  inactiveOnly: boolean;
  viewMode: UserListViewMode;
  displayedColumns: string[];
  columnDefinitions: ColumnDefinition[];
  userDataAttributes: UserDataAttributeGroup[];
  rows: UserListRow[];
  filteredRows: UserListRow[];
  pagedRows: UserListRow[];
  pageIndex: number;
  pageSize: number;
  scrollTop: number;
  sortActive: string;
  sortDirection: SortDirection;
  userManagementRole: boolean;
  refreshing: boolean;
}

const initialState: HomeUserListState = {
  status: 'idle',
  errorMessage: null,
  breadcrumbs: [],
  filterText: '',
  inactiveOnly: false,
  viewMode: 'table',
  displayedColumns: [],
  columnDefinitions: [],
  userDataAttributes: [],
  rows: [],
  filteredRows: [],
  pagedRows: [],
  pageIndex: 0,
  pageSize: 10,
  scrollTop: 0,
  sortActive: 'lastUpdated',
  sortDirection: 'desc',
  userManagementRole: false,
  refreshing: false,
};

@Injectable()
export class HomeUserListViewModel extends BaseViewModel<HomeUserListState> {
  private allRows: UserListRow[] = [];
  private readonly protocolLocale: 'ja' | 'en' | 'zh-TW';

  constructor(
    private readonly common: DatabaseCacheService,
    private readonly listStateService: HomeUserListStateService,
    private readonly paginationService: PaginationService,
    private readonly scrollService: ScrollService,
    private readonly msalService: MsalService,
    private readonly destroyRef: DestroyRef,
    @Inject(LOCALE_ID) localeId: string,
  ) {
    super(initialState);
    const lower = (localeId || '').toLowerCase();
    this.protocolLocale = lower.startsWith('en') ? 'en' : lower.startsWith('zh') ? 'zh-TW' : 'ja';

    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshFromCache());
  }

  init(): void {
    this.refreshFromCache();
  }

  updateFilterText(value: string): void {
    this.persistFilterText(value);
    this.patchState({ filterText: value, pageIndex: 0 });
    this.recalculateRows();
  }

  toggleInactiveOnly(value: boolean): void {
    this.persistInactiveOnly(value);
    this.patchState({ inactiveOnly: value, pageIndex: 0 });
    this.recalculateRows();
  }

  updatePagination(pageIndex: number, pageSize: number): void {
    const cacheKey = this.getStateCacheKey();
    if (cacheKey) {
      this.paginationService.setPageIndex(cacheKey, pageIndex);
      this.paginationService.setPageSize(cacheKey, pageSize);
    }
    this.patchState({ pageIndex, pageSize });
    this.recalculatePagedRows();
  }

  updateViewMode(mode: UserListViewMode): void {
    const cacheKey = this.getStateCacheKey();
    if (cacheKey) {
      this.listStateService.setViewMode(cacheKey, mode);
    }
    this.patchState({ viewMode: mode });
  }

  toggleColumnVisibility(columnDef: string): void {
    const definitions = this.currentState.columnDefinitions.map(cd =>
      cd.def === columnDef && !cd.locked
        ? { ...cd, visible: !cd.visible }
        : cd
    );
    const displayedColumns = definitions
      .filter(cd => cd.visible)
      .map(cd => cd.def);
    this.patchState({ columnDefinitions: definitions, displayedColumns });
  }

  refreshData(): void {
    this.patchState({ refreshing: true });
    this.common.refetchUsersList();
  }

  updateSort(active: string, direction: SortDirection): void {
    this.patchState({
      sortActive: active,
      sortDirection: direction,
    });
    this.recalculateRows();
  }

  updateScrollPosition(scrollTop: number): void {
    const cacheKey = this.getStateCacheKey();
    if (cacheKey) {
      this.scrollService.setScrollPosition(cacheKey, scrollTop);
    }
    this.patchState({ scrollTop });
  }

  private refreshFromCache(): void {
    const selectedSection = this.common.getSelectedSection();
    const breadcrumbs = selectedSection
      ? [
          { label: '', icon: 'home', link: ['/home', selectedSection.sectionId] },
          {
            label: HOME_USER_LIST_I18N.breadcrumb.userList,
            icon: '',
            link: ['/home', selectedSection.sectionId, 'users'],
          },
        ]
      : [];

    const rawUsersList = this.common.getUsersList() as UserListRow[] | null | undefined;

    // When usersList is null, data is being fetched — show ghost skeleton.
    // Generate placeholder rows from the NEW section's column definitions
    // so the skeleton uses the exact same template & column structure as real content.
    if (rawUsersList == null) {
      this.allRows = [];
      const cacheKey = this.getStateCacheKey();
      const savedViewMode = cacheKey
        ? this.listStateService.getViewMode(cacheKey)
        : null;
      const pageSize = cacheKey
        ? this.paginationService.getPageSize(cacheKey) ?? 10
        : 10;

      const userDataAttributes =
        (this.common.getSelectedSectionGroupUserDataAttributes() as
          | UserDataAttributeGroup[]
          | null
          | undefined) ?? [];
      const columnDefinitions = this.buildColumnDefinitions(userDataAttributes);
      const displayedColumns = columnDefinitions
        .filter(cd => cd.visible)
        .map(cd => cd.def);

      const placeholderRows = this.buildPlaceholderRows(pageSize);

      this.patchState({
        status: 'loading',
        refreshing: false,
        breadcrumbs,
        viewMode: savedViewMode ?? this.currentState.viewMode,
        columnDefinitions,
        displayedColumns,
        userDataAttributes,
        rows: [],
        filteredRows: placeholderRows,
        pagedRows: placeholderRows,
        pageIndex: 0,
        pageSize,
      });
      return;
    }

    this.allRows = rawUsersList;

    const userDataAttributes =
      (this.common.getSelectedSectionGroupUserDataAttributes() as
        | UserDataAttributeGroup[]
        | null
        | undefined) ?? [];
    const columnDefinitions = this.buildColumnDefinitions(userDataAttributes);
    const displayedColumns = columnDefinitions
      .filter(cd => cd.visible)
      .map(cd => cd.def);

    const cacheKey = this.getStateCacheKey();
    const filterText = cacheKey
      ? this.listStateService.getFilterText(cacheKey)
      : '';
    const inactiveOnly = cacheKey
      ? this.listStateService.getInactiveOnly(cacheKey)
      : false;
    const pageIndex = cacheKey
      ? this.paginationService.getPageIndex(cacheKey) ?? 0
      : 0;
    const pageSize = cacheKey
      ? this.paginationService.getPageSize(cacheKey) ?? 10
      : 10;
    const scrollTop = cacheKey
      ? this.scrollService.getScrollPosition(cacheKey) ?? 0
      : 0;

    // viewMode: saved preference > default table
    const savedViewMode = cacheKey
      ? this.listStateService.getViewMode(cacheKey)
      : null;
    const viewMode: UserListViewMode = savedViewMode ?? 'table';

    // Role extraction (following home-nav pattern)
    const selectedSectionWithRole = this.common.getSelectedSectionWithRole();
    const roles: string[] = selectedSectionWithRole?.permissions?.role ?? [];
    const userManagementRole = roles.includes('USER_MANAGEMENT');

    this.patchState({
      status: 'success',
      refreshing: false,
      breadcrumbs,
      filterText,
      inactiveOnly,
      viewMode,
      columnDefinitions,
      displayedColumns,
      userDataAttributes,
      rows: this.allRows,
      pageIndex,
      pageSize,
      scrollTop,
      userManagementRole,
    } as Partial<HomeUserListState>);

    this.recalculateRows();
  }

  private recalculateRows(): void {
    const { filterText, inactiveOnly, sortActive, sortDirection } = this.currentState;
    const filtered = this.filterRows(this.allRows, filterText, inactiveOnly);
    const sorted = this.sortRows(filtered, sortActive, sortDirection);
    this.patchState({
      filteredRows: sorted,
      pageIndex: Math.min(this.currentState.pageIndex, Math.max(Math.ceil(sorted.length / this.currentState.pageSize) - 1, 0)),
    });
    this.recalculatePagedRows();
  }

  private recalculatePagedRows(): void {
    const { filteredRows, pageIndex, pageSize } = this.currentState;
    const paged = this.sliceRows(filteredRows, pageIndex, pageSize);
    this.patchState({ pagedRows: paged });
  }

  private filterRows(
    rows: UserListRow[],
    filterText: string,
    inactiveOnly: boolean,
  ): UserListRow[] {
    const terms = this.normalize(filterText);
    return rows.filter(row => {
      if (inactiveOnly && !this.isInactive(row)) {
        return false;
      }
      if (!terms.length) {
        return true;
      }
      return terms.every(term => this.buildSearchTarget(row).includes(term));
    });
  }

  private sortRows(
    rows: UserListRow[],
    active: string,
    direction: SortDirection,
  ): UserListRow[] {
    if (!direction) {
      return rows.slice();
    }
    const sorted = rows.slice().sort((a, b) => {
      switch (active) {
        case 'userRecord':
          return this.buildSortableName(a).localeCompare(this.buildSortableName(b));
        case 'lastUpdated':
          return (
            this.getSortableTimestamp(a.userStatistics?.data?.lastUpdatedProtocolCreatedAt) -
            this.getSortableTimestamp(b.userStatistics?.data?.lastUpdatedProtocolCreatedAt)
          );
        case 'lastUpdatedProtocol':
          return getRecordTypePrimaryLabel(
            a.userStatistics?.data?.lastUpdatedProtocol,
            a.userStatistics?.data?.lastUpdatedProtocol,
            this.protocolLocale,
          ).localeCompare(
            getRecordTypePrimaryLabel(
              b.userStatistics?.data?.lastUpdatedProtocol,
              b.userStatistics?.data?.lastUpdatedProtocol,
              this.protocolLocale,
            ),
          );
        default:
          return 0;
      }
    });
    return direction === 'asc' ? sorted : sorted.reverse();
  }

  private sliceRows(rows: UserListRow[], pageIndex: number, pageSize: number): UserListRow[] {
    const start = pageIndex * pageSize;
    return rows.slice(start, start + pageSize);
  }

  private isInactive(row: UserListRow): boolean {
    const lastUpdated =
      row.userStatistics?.data?.lastUpdatedProtocolCreatedAt ?? null;
    if (!lastUpdated) {
      return true;
    }
    const parsed = parseDateInput(lastUpdated);
    if (!parsed) {
      return true;
    }
    const updatedAt = parsed.getTime();
    const diffDays = (Date.now() - updatedAt) / (1000 * 60 * 60 * 24);
    return diffDays >= 90;
  }

  private buildSearchTarget(row: UserListRow): string {
    const protocol = row.userStatistics?.data?.lastUpdatedProtocol ?? '';
    const parts: string[] = [
      row.user.code ?? '',
      row.user.lastName ?? '',
      row.user.firstName ?? '',
      row.user.birthDate ?? '',
      protocol,
      buildRecordTypeSearchText(protocol, protocol),
      row.userStatistics?.data?.lastUpdatedProtocolCreatedAt ?? '',
    ];
    this.currentState.userDataAttributes.forEach(group => {
      group?.dataItems?.forEach(attr => {
        if (attr?.enabled && attr?.showOnSummary) {
          const value = row.userData?.newest?.data?.[attr.name];
          if (value) {
            parts.push(value);
          }
        }
      });
    });
    return this.normalize(parts.join(' ')).join(' ');
  }

  private buildSortableName(row: UserListRow): string {
    return this.normalize(
      `${row.user.code ?? ''} ${row.user.lastName ?? ''} ${row.user.firstName ?? ''}`,
    ).join(' ');
  }

  private normalize(value: string): string[] {
    return value
      .normalize('NFKC')
      .toLowerCase()
      .replace(/[\u3041-\u3096]/g, (char: string) =>
        String.fromCharCode(char.charCodeAt(0) + 0x60),
      )
      .split(/\s+/)
      .filter(Boolean);
  }

  private getSortableTimestamp(dateStr?: string | null): number {
    const timestamp = getTimestamp(dateStr);
    if (timestamp == null) {
      return Number.NEGATIVE_INFINITY;
    }
    return timestamp;
  }

  private buildColumnDefinitions(attributes: UserDataAttributeGroup[]): ColumnDefinition[] {
    const labels = {
      userRecord: $localize`:@@home.homeUserList.column.userRecord:ユーザー`,
      sex: $localize`:@@home.homeUserList.column.sex:性別`,
      birthDate: $localize`:@@home.homeUserList.column.birthDate:生年月日`,
      lastUpdatedProtocol: $localize`:@@home.homeUserList.column.lastUpdatedProtocol:最終更新プロトコル`,
      lastUpdated: $localize`:@@home.homeUserList.column.lastUpdated:最終更新日`,
    };

    const defs: ColumnDefinition[] = [
      { def: 'userRecord', label: labels.userRecord, visible: true, locked: true },
      { def: 'sex', label: labels.sex, visible: true },
      { def: 'birthDate', label: labels.birthDate, visible: true },
      { def: 'lastUpdatedProtocol', label: labels.lastUpdatedProtocol, visible: true },
    ];

    // Dynamic columns
    attributes.forEach((group, i) => {
      group?.dataItems?.forEach((attr, j) => {
        if (attr?.enabled && attr?.showOnSummary) {
          defs.push({
            def: `column${i}${j}`,
            label: this.common.getDispName(attr),
            visible: true,
          });
        }
      });
    });

    defs.push({ def: 'lastUpdated', label: labels.lastUpdated, visible: true });
    defs.push({ def: 'actions', label: '', visible: true, locked: true });

    return defs;
  }

  private buildPlaceholderRows(count: number): UserListRow[] {
    return Array.from({ length: count }, (_, i) => ({
      user: {
        userId: `__placeholder_${i}`,
        code: '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0',
        lastName: '\u00A0\u00A0\u00A0\u00A0',
        firstName: '\u00A0\u00A0\u00A0\u00A0',
        sex: 'male',
        birthDate: '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0',
      },
      userStatistics: {
        data: {
          lastUpdatedProtocol: '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0',
          lastUpdatedProtocolCreatedAt: '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0',
        },
      },
    }));
  }

  private getStateCacheKey(): string | null {
    const sectionId = this.common.getSelectedSection()?.sectionId;
    const account = this.msalService.instance.getActiveAccount();
    const loginUserId =
      (account?.idTokenClaims as Record<string, unknown> | undefined)?.['sub'];

    if (!sectionId || typeof loginUserId !== 'string') {
      return null;
    }

    return `${sectionId}-${loginUserId}`;
  }

  private persistFilterText(value: string): void {
    const cacheKey = this.getStateCacheKey();
    if (!cacheKey) {
      return;
    }
    this.listStateService.setFilterText(cacheKey, value);
  }

  private persistInactiveOnly(value: boolean): void {
    const cacheKey = this.getStateCacheKey();
    if (!cacheKey) {
      return;
    }
    this.listStateService.setInactiveOnly(cacheKey, value);
  }
}
